import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../UI/dialog';
import { Button } from '../UI/button';
import { useLocale } from '../../contexts/LocaleContext';
import {
  HEADER_ORIENTATION,
  IMPORT_DTO_FIELDS,
  buildMappedRows,
  guessDtoMapping,
  mappedRowsHaveRequiredTicker,
  parseCsvDataForMapping,
} from '../../utils/csvDtoMapper';
import { buildBatchPayloadFromMappedRows } from '../../utils/importDtoMappers';
import {
  detectSpreadsheetOrientation,
  isNatureCrunchMatrixFile,
  parseSpreadsheetBuffer,
  readFileAsArrayBuffer,
  readSpreadsheetMatrix,
} from '../../utils/spreadsheetCompanyImport';
import { cn } from '../../lib/utils';
import CsvColumnConnector from './CsvColumnConnector';

function CsvMapperModal({ open, importKey, file, onClose, onConfirm }) {
  const { t } = useLocale();
  const [buffer, setBuffer] = useState(null);
  const [isMatrixLayout, setIsMatrixLayout] = useState(false);
  const [orientation, setOrientation] = useState(HEADER_ORIENTATION.ROW);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const dtoFields = IMPORT_DTO_FIELDS[importKey] || [];

  useEffect(() => {
    if (!open || !file) return;

    setLoading(true);
    setError('');
    setBuffer(null);
    setIsMatrixLayout(false);

    readFileAsArrayBuffer(file)
      .then((result) => {
        const sheetMatrix = readSpreadsheetMatrix(result);
        const detectedOrientation =
          detectSpreadsheetOrientation(sheetMatrix) === 'column'
            ? HEADER_ORIENTATION.COLUMN
            : HEADER_ORIENTATION.ROW;

        setBuffer(result);
        setIsMatrixLayout(
          (importKey === 'supplierFacilities' || importKey === 'suppliers') &&
            isNatureCrunchMatrixFile(sheetMatrix)
        );
        setOrientation(detectedOrientation);

        const parsed = parseSpreadsheetBuffer(result, detectedOrientation);
        setHeaders(parsed.headers || []);
        setRows(parsed.rows || []);
        setMapping(guessDtoMapping(parsed.headers || [], dtoFields, importKey));
      })
      .catch(() => {
        setError(t('csvMapperReadError'));
        setHeaders([]);
        setRows([]);
        setMapping({});
      })
      .finally(() => setLoading(false));
  }, [open, file, importKey, dtoFields, t]);

  useEffect(() => {
    if (!buffer || !open) return;
    try {
      const parsed = parseCsvDataForMapping(buffer, orientation);
      setHeaders(parsed.headers || []);
      setRows(parsed.rows || []);
      setMapping(guessDtoMapping(parsed.headers || [], dtoFields, importKey));
      setError('');
    } catch (_) {
      setError(t('csvMapperParseError'));
      setHeaders([]);
      setRows([]);
      setMapping({});
    }
  }, [buffer, orientation, dtoFields, importKey, open, t]);

  const mappedRows = useMemo(
    () => buildMappedRows(rows, mapping, dtoFields),
    [rows, mapping, dtoFields]
  );

  const batchPayload = useMemo(
    () => buildBatchPayloadFromMappedRows(importKey, mappedRows),
    [importKey, mappedRows]
  );

  const batchItemCount = useMemo(() => {
    if (batchPayload.kind === 'single') {
      return batchPayload.payload ? 1 : 0;
    }
    return Array.isArray(batchPayload.payload) ? batchPayload.payload.length : 0;
  }, [batchPayload]);

  const mappedPreview = useMemo(() => mappedRows.slice(0, 5), [mappedRows]);

  const requiresSupplierTicker =
    importKey === 'supplierFacilities' || importKey === 'suppliers';

  const tickerMappingMissing = useMemo(() => {
    if (!requiresSupplierTicker || !rows.length) return false;
    return !mapping.ticker;
  }, [requiresSupplierTicker, rows.length, mapping.ticker]);

  const tickerValuesMissing = useMemo(() => {
    if (!requiresSupplierTicker || !rows.length) return false;
    return Boolean(mapping.ticker) && !mappedRowsHaveRequiredTicker(mappedRows);
  }, [requiresSupplierTicker, rows.length, mapping.ticker, mappedRows]);

  const canConfirm = useMemo(() => {
    if (loading || Boolean(error)) return false;
    if (!dtoFields.length || !rows.length) return false;
    if (!dtoFields.some((field) => Boolean(mapping[field]))) return false;
    if (requiresSupplierTicker) {
      return mappedRowsHaveRequiredTicker(mappedRows) && batchItemCount > 0;
    }
    return batchItemCount > 0;
  }, [
    loading,
    error,
    dtoFields,
    rows.length,
    mapping,
    requiresSupplierTicker,
    mappedRows,
    batchItemCount,
  ]);

  const handleConfirm = () => {
    onConfirm({ importKey, mappedRows });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('csvMapperTitle')}</DialogTitle>
          <DialogDescription>{t('csvMapperBatchDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-secondary">
              {t('csvMapperOrientationLabel')}
            </span>
            <div className="inline-flex rounded-lg border border-border-light p-1">
              {[HEADER_ORIENTATION.ROW, HEADER_ORIENTATION.COLUMN].map((value) => (
                <button
                  key={value}
                  type="button"
                  disabled={loading}
                  onClick={() => setOrientation(value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    orientation === value
                      ? 'bg-primary-500 text-primary-900'
                      : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
                  )}
                >
                  {value === HEADER_ORIENTATION.ROW
                    ? t('csvMapperOrientationRow')
                    : t('csvMapperOrientationColumn')}
                </button>
              ))}
            </div>
          </div>

          {orientation === HEADER_ORIENTATION.COLUMN ? (
            <p className="text-sm text-text-secondary">{t('csvMapperOrientationColumnDetected')}</p>
          ) : null}

          {isMatrixLayout ? (
            <p className="rounded-lg border border-primary-200 bg-primary-50/40 p-3 text-sm text-text-secondary">
              {t('tenantCsvMapperMatrixModeDescription')}
            </p>
          ) : null}

          {batchItemCount > 0 ? (
            <p className="text-sm font-medium text-text-primary tabular-nums">
              {t('csvMapperBatchReadyCount', { count: batchItemCount })}
            </p>
          ) : null}

          {error && <p className="text-sm text-danger-600">{error}</p>}

          {importKey === 'suppliers' ? (
            <p className="text-sm text-text-secondary">{t('tenantCsvMapperSuppliersIdHint')}</p>
          ) : null}
          {importKey === 'supplierFacilities' ? (
            <p className="text-sm text-text-secondary">{t('tenantCsvMapperSupplierFacilitiesHint')}</p>
          ) : null}

          {tickerMappingMissing ? (
            <p className="text-sm text-danger-600">{t('tenantCsvMapperTickerRequired')}</p>
          ) : null}
          {tickerValuesMissing ? (
            <p className="text-sm text-danger-600">{t('tenantCsvMapperTickerEmpty')}</p>
          ) : null}

          {!error && !loading && headers.length > 0 ? (
            <CsvColumnConnector
              headers={headers}
              dtoFields={dtoFields}
              mapping={mapping}
              onMappingChange={setMapping}
            />
          ) : null}

          {loading ? (
            <p className="text-sm text-text-secondary">{t('csvMapperLoading')}</p>
          ) : null}

          {mappedPreview.length > 0 ? (
            <div>
              <p className="mb-2 text-sm font-medium text-text-primary">{t('csvMapperPreviewTitle')}</p>
              <div className="overflow-x-auto rounded-lg border border-border-light">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-background-secondary">
                      {dtoFields.map((field) => (
                        <th key={`head-${field}`} className="px-2 py-2 font-medium text-text-secondary">
                          {field}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mappedPreview.map((row, idx) => (
                      <tr key={`preview-${idx}`} className="border-t border-border-light">
                        {dtoFields.map((field) => (
                          <td key={`cell-${idx}-${field}`} className="px-2 py-2 text-text-primary">
                            {row[field] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canConfirm || loading || Boolean(error)}>
            {t('csvMapperApply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CsvMapperModal;
